import React, { createContext, useContext } from 'react';
import type { ApiInvitation, TemplateThemeConfig } from '@/src/types';

export interface ResolvedTemplateTokens {
  color: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textMuted: string;
    border: string;
  };
  font: {
    serif: string;
    sans: string;
    script: string;
  };
  radius: {
    sm: number;
    md: number;
    lg: number;
    full: number;
  };
}

export interface TemplateRenderContext {
  data: ApiInvitation;
  tokens: ResolvedTemplateTokens;
  mode: 'preview' | 'live';
}

export const DEFAULT_TOKENS: ResolvedTemplateTokens = {
  color: {
    primary: '#574eb1',
    secondary: '#c39845',
    accent: '#e8d5c4',
    background: '#fcf8ff',
    surface: '#ffffff',
    text: '#1c1b21',
    textMuted: '#6f6a78',
    border: '#e5deef',
  },
  font: {
    serif: 'Georgia, serif',
    sans: 'Inter, ui-sans-serif, system-ui, sans-serif',
    script: 'Georgia, serif',
  },
  radius: {
    sm: 4,
    md: 8,
    lg: 16,
    full: 9999,
  },
};

export function resolveTheme(theme?: TemplateThemeConfig | null): ResolvedTemplateTokens {
  const color = theme?.tokens?.color ?? {};
  const font = theme?.tokens?.font ?? {};
  const radius = theme?.tokens?.radius ?? {};

  return {
    color: {
      primary: color.primary ?? DEFAULT_TOKENS.color.primary,
      secondary: color.secondary ?? DEFAULT_TOKENS.color.secondary,
      accent: color.accent ?? DEFAULT_TOKENS.color.accent,
      background: color.background ?? DEFAULT_TOKENS.color.background,
      surface: color.surface ?? DEFAULT_TOKENS.color.surface,
      text: color.text ?? DEFAULT_TOKENS.color.text,
      textMuted: color.textMuted ?? DEFAULT_TOKENS.color.textMuted,
      border: color.border ?? DEFAULT_TOKENS.color.border,
    },
    font: {
      serif: font.serif ?? DEFAULT_TOKENS.font.serif,
      sans: font.sans ?? DEFAULT_TOKENS.font.sans,
      script: font.script ?? DEFAULT_TOKENS.font.script,
    },
    radius: {
      sm: Number(radius.sm ?? DEFAULT_TOKENS.radius.sm),
      md: Number(radius.md ?? DEFAULT_TOKENS.radius.md),
      lg: Number(radius.lg ?? DEFAULT_TOKENS.radius.lg),
      full: Number(radius.full ?? DEFAULT_TOKENS.radius.full),
    },
  };
}

const TemplateCtx = createContext<TemplateRenderContext | null>(null);

export function TemplateContextProvider({
  value,
  children,
}: {
  value: TemplateRenderContext;
  children: React.ReactNode;
}) {
  return <TemplateCtx.Provider value={value}>{children}</TemplateCtx.Provider>;
}

export function useTemplateContext() {
  const ctx = useContext(TemplateCtx);
  if (!ctx) throw new Error('Template renderer context is missing');
  return ctx;
}
